'use client';

import { useMemo } from 'react';
import { useCalendarStore } from '@/stores/calendar-store';
import { getCalendarAdapter } from '@/lib/calendar-systems';
import { formatDateLong } from '@/lib/date-format';

interface DualDateLabelProps {
  /** Gregorian date in YYYY-MM-DD form (the canonical event.dateStart shape). */
  iso: string;
  /** Optional override for the language used for the Gregorian primary side. */
  lang?: string;
  /**
   * "auto" (default) — render Gregorian + secondary in active system if non-Gregorian
   * "system-first" — secondary side wins (active system primary)
   * "gregorian-only" — never show secondary, regardless of toggle
   */
  variant?: 'auto' | 'system-first' | 'gregorian-only';
  className?: string;
}

/**
 * Display a date with optional secondary label in the user's active calendar
 * system. Server still renders Gregorian (for SEO + cache stability); the
 * secondary label is appended client-side after hydration when the active
 * system differs from Gregorian.
 *
 * Example output (en, system=islamic-umalqura):
 *   "Wed, Jun 17, 2026 · 1 Muharram 1448"
 */
export function DualDateLabel({ iso, lang: langProp, variant = 'auto', className }: DualDateLabelProps) {
  const storeLang = useCalendarStore((s) => s.lang) || 'en';
  const lang = langProp ?? storeLang;
  const calendarSystem = useCalendarStore((s) => s.calendarSystem);

  const gregorianLabel = formatDateLong(iso, lang);

  const secondary = useMemo(() => {
    if (variant === 'gregorian-only' || calendarSystem === 'gregorian') return null;
    const adapter = getCalendarAdapter(calendarSystem);
    // Parse YYYY-MM-DD as a UTC date to avoid TZ drift.
    const [y, m, d] = iso.split('-').map(Number);
    const greg = new Date(Date.UTC(y, m - 1, d));
    const parts = adapter.fromGregorian(greg);
    const yearDisplay = parts.eraYear ?? parts.year;
    const monthName = adapter.monthName(parts.year, parts.month, lang);
    return `${parts.day} ${monthName} ${yearDisplay}`;
  }, [iso, lang, calendarSystem, variant]);

  if (!secondary) return <span className={className}>{gregorianLabel}</span>;

  if (variant === 'system-first') {
    return (
      <span className={className}>
        {secondary}
        <span className="opacity-70"> · {gregorianLabel}</span>
      </span>
    );
  }

  return (
    <span className={className}>
      {gregorianLabel}
      <span className="opacity-70"> · {secondary}</span>
    </span>
  );
}

interface AltCalendarDateProps {
  /** Gregorian YYYY-MM-DD */
  iso: string;
  /** Gregorian YYYY-MM-DD if multi-day; falls back to iso when omitted. */
  endIso?: string;
  className?: string;
  /** Optional override; defaults to store.lang. */
  lang?: string;
  /** Wrap the rendered label with this prefix (e.g. " · "). Hidden when no secondary. */
  prefix?: string;
}

/**
 * Render ONLY the active calendar system's date, or nothing when calendarSystem === 'gregorian'.
 * Pair this next to an existing Gregorian <time> element to add a secondary label
 * post-hydration without disturbing the SSR'd Gregorian primary.
 *
 * Example output (en, system=islamic-umalqura):
 *   " · 1 Muharram 1448"
 */
export function AltCalendarDate({ iso, endIso, className, lang: langProp, prefix = ' · ' }: AltCalendarDateProps) {
  const storeLang = useCalendarStore((s) => s.lang) || 'en';
  const lang = langProp ?? storeLang;
  const calendarSystem = useCalendarStore((s) => s.calendarSystem);

  const label = useMemo(() => {
    if (calendarSystem === 'gregorian') return null;
    const adapter = getCalendarAdapter(calendarSystem);
    const parse = (s: string) => {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(Date.UTC(y, m - 1, d));
    };
    const startParts = adapter.fromGregorian(parse(iso));
    const yearDisplay = startParts.eraYear ?? startParts.year;
    const monthName = adapter.monthName(startParts.year, startParts.month, lang);
    const startStr = `${startParts.day} ${monthName} ${yearDisplay}`;

    if (!endIso || endIso === iso) return startStr;
    const endParts = adapter.fromGregorian(parse(endIso));
    if (endParts.year === startParts.year && endParts.month === startParts.month) {
      return `${startParts.day}–${endParts.day} ${monthName} ${yearDisplay}`;
    }
    const endMonthName = adapter.monthName(endParts.year, endParts.month, lang);
    const endYearDisplay = endParts.eraYear ?? endParts.year;
    return `${startStr} – ${endParts.day} ${endMonthName} ${endYearDisplay}`;
  }, [iso, endIso, lang, calendarSystem]);

  if (!label) return null;

  return <span className={className}>{prefix}{label}</span>;
}
